import React, { useState } from "react";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useWMSchedules, weekendApi } from "../../../../../api/weekend";
import { HGBugsnagNotify } from "../../../../../helpers/bugsnag";
import { weekOfString } from "../../../../../helpers/dateHelpers";
import { PublicTalkAssignment } from "../../../../../types/scheduling/weekend";
import { HourglassDatePicker, MenuType } from "../../../date";
import { SpeakerName } from "../../wm_common";
import { nameOfUser } from "../../../../../helpers/user";
import { DateRange } from "../../../../../types/date";

interface OutgoingMoveModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  out: PublicTalkAssignment;
  refresh: () => void;
  langGroupId: number;
}

export const OutgoingMoveModal: React.FC<OutgoingMoveModalProps> = (props) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState("");
  const [toSwap, setToSwap] = useState<PublicTalkAssignment>();
  // get the schedule for the selected date
  const workingDate = selectedDate || props.out.date;
  const currentDate = weekOfString(workingDate);
  const dates: DateRange = {
    from: currentDate,
    to: currentDate,
  };
  const scheduleQuery = useWMSchedules(dates.from, dates.to, props.langGroupId, {
    enabled: !!workingDate && !!dates.from && !!dates.to,
  });

  const handleClose = () => {
    props.setShow(false);
    setToSwap(undefined);
    setSelectedDate("");
  };

  const handleMove = async () => {
    try {
      await weekendApi.setOutSchedule(
        {
          id: props.out.id,
          date: selectedDate,
          assignee: props.out.speaker?.id ?? 0,
          congregation: props.out.congregation?.id ?? 0,
          talk: props.out.public_talk?.number ?? 0,
        },
        props.langGroupId,
      );
      props.refresh();
      handleClose();
    } catch (err: any) {
      console.error("wm out move error", err);
      HGBugsnagNotify("wmOutgoingMove", err);
    }
  };

  const handleSwap = async () => {
    if (!toSwap) return;
    try {
      await weekendApi.setOutSchedule(
        {
          id: props.out.id,
          date: selectedDate,
          assignee: props.out.speaker?.id ?? 0,
          congregation: props.out.congregation?.id ?? 0,
          talk: props.out.public_talk?.number ?? 0,
        },
        props.langGroupId,
      );
      await weekendApi.setOutSchedule(
        {
          id: toSwap.id,
          date: props.out.date,
          assignee: toSwap.speaker?.id ?? 0,
          congregation: toSwap.congregation?.id ?? 0,
          talk: toSwap.public_talk?.number ?? 0,
        },
        props.langGroupId,
      );

      props.refresh();
      handleClose();
    } catch (err: any) {
      console.error("wm out swap error", err);
      HGBugsnagNotify("wmOutgoingSwap", err);
    }
  };

  const selectedWeek = weekOfString(selectedDate);
  const outgoingOnSelectedDate =
    scheduleQuery.data
      ?.filter((s) => weekOfString(s.date) === selectedWeek)
      ?.flatMap((s) => s.out)
      ?.filter((pt) => !!pt.speaker) ?? [];

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.talk-mover")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <HourglassDatePicker
          menuType={MenuType.Day}
          date={selectedDate || weekOfString(props.out.date, props.out.congregation?.wm_dow ?? 7)}
          onDateChange={setSelectedDate}
          required
        />

        {outgoingOnSelectedDate.length > 0 && (
          <div className="mt-4">
            <p>{t("schedules.weekend.outgoing-swap-prompt")}</p>
            <Dropdown>
              <Dropdown.Toggle variant="secondary" className="dropdown-bounded">
                {toSwap?.speaker ? nameOfUser(toSwap.speaker) : t("general.none-selected")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {outgoingOnSelectedDate.map((pt) => (
                  <Dropdown.Item key={pt.id} onClick={() => setToSwap(pt)}>
                    {!!pt.speaker && <SpeakerName speaker={pt.speaker} />}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        {outgoingOnSelectedDate.length > 0 && !!toSwap && (
          <Button variant="danger" onClick={handleSwap}>
            {t("schedules.weekend.swap")}
          </Button>
        )}
        <Button variant="danger" onClick={handleMove}>
          {t("schedules.weekend.move-assignment")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
